import { useEffect } from "react";
import { RotateCcw, Volume2 } from "lucide-react";
import { SporeGlyph } from "./SporeGlyph";
import { playNarration, stopNarration } from "../../lib/narration";

export const UnlockOverlay = ({ open, message, finale, subject, specimens, onReset, onClose }) => {
  useEffect(() => {
    if (!open) return undefined;
    const t = setTimeout(() => playNarration("finale"), 1200);
    return () => { clearTimeout(t); stopNarration(); };
  }, [open]);

  if (!open) return null;
  return (
    <div data-testid="vocabulary-unlocked-modal" className="fixed inset-0 z-50 overflow-y-auto bg-[#050807]/95 backdrop-blur-xl">
      <div className="spore-field" />
      <div className="relative mx-auto flex min-h-full max-w-5xl flex-col justify-center px-6 py-14">
        <p className="lab-label text-rose-400 unlock-in" style={{ animationDelay: "0.1s" }}>
          Spore Lab · case closed // all {specimens.length} memories recovered
        </p>
        <h1 data-testid="vocabulary-unlocked-title" className="unlock-title spore-headline spore-headline-chord mt-4 text-5xl sm:text-7xl lg:text-8xl unlock-in" style={{ animationDelay: "0.3s" }}>
          Subject{" "}<br />identified
        </h1>
        {subject && (
          <div data-testid="unlock-twist" className="mt-6 unlock-in" style={{ animationDelay: "0.5s" }}>
            <p className="spore-headline text-3xl sm:text-5xl">{subject.name}</p>
            <p className="mt-2 font-mono text-sm sm:text-base uppercase tracking-[0.15em] text-amber-300">
              {subject.nickname ? `"${subject.nickname}" · ` : ""}{subject.home} · son of {subject.father} - present in this room
            </p>
          </div>
        )}
        <div className="mt-8 flex flex-wrap gap-3 unlock-in" style={{ animationDelay: "0.6s" }}>
          {specimens.map((s) => (
            <SporeGlyph key={s.id} word={s.spore_word} size={34} className="text-cyan-300/80" />
          ))}
        </div>
        <div className="mt-10 grid gap-6 lg:grid-cols-2 unlock-in" style={{ animationDelay: "0.9s" }}>
          <div className="border border-amber-400/30 bg-slate-950/80 p-6 sm:p-8">
            <div className="flex items-center justify-between gap-3">
              <p className="scientist-label">The Scientist · conclusion</p>
              <button data-testid="unlock-replay-narration" onClick={() => playNarration("finale")} className="lab-icon-btn h-8 w-8" title="Replay"><Volume2 size={14} /></button>
            </div>
            <p data-testid="unlock-finale-text" className="scientist-line mt-4 text-base sm:text-lg leading-relaxed whitespace-pre-line">{finale}</p>
          </div>
          <div className="border border-emerald-400/30 bg-slate-950/80 p-6 sm:p-8">
            <p className="lab-label mb-4">The Darkness speaks for itself</p>
            <p data-testid="vocabulary-final-message" className="font-mono text-base sm:text-xl leading-relaxed text-emerald-200 whitespace-pre-line">
              {message}
            </p>
          </div>
        </div>
        <div className="mt-10 flex flex-wrap gap-4 unlock-in" style={{ animationDelay: "1.2s" }}>
          <button data-testid="unlock-play-again-button" onClick={onReset} className="lab-btn-primary">
            <RotateCcw size={18} /> Begin a new run
          </button>
          <button data-testid="unlock-view-lexicon-button" onClick={onClose} className="lab-btn-ghost">
            View case file
          </button>
        </div>
      </div>
    </div>
  );
};
